<template>
  <div class="d-flex flex-column min-vh-100">
    <v-container class="flex-grow-1 d-flex">
      <v-row justify="center" class="w-100">
        <v-col cols="12" sm="10" md="8" lg="6">
          <div class="text-center mb-8 mt-8">
            <h1 class="text-primary weight-600">Quick Start Clinical Review</h1>
            <p class="text-grey mt-2">
              Upload a document and enter the details below to run a clinical policy review right away.
            </p>
          </div>

          <v-card class="pa-6">
            <v-card-text>
              <v-form ref="formRef" @submit.prevent="handleSubmit">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="patientFirstName"
                      label="Patient First Name"
                      :rules="[rules.required]"
                      class="mb-2 lh--force-white-background" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="patientLastName"
                      label="Patient Last Name"
                      :rules="[rules.required]"
                      class="mb-2 lh--force-white-background" />
                  </v-col>
                </v-row>

                <TextDateInput
                  v-model="patientDobDate"
                  label="Patient Date of Birth"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <v-select
                  v-model="referralStatus"
                  label="Initial / Continuation of Therapy"
                  :items="referralStatusItems"
                  item-title="title"
                  item-value="value"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="providerNpi"
                  label="Provider NPI"
                  :rules="[rules.required, rules.npi]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="drugName"
                  label="Drug Name"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <RemoteAutocomplete
                  v-model="diagnosisIcd10Code"
                  :remote-search="onChangeIcd10Code"
                  label="Diagnosis Code (ICD-10)"
                  :rules="[rules.required, icd10CodeRule]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="phoneNumber"
                  label="Phone Number (optional)"
                  type="tel"
                  :rules="[usPhoneRule]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="payorName"
                  label="Insurance Payor (optional)"
                  class="mb-4 lh--force-white-background" />

                <h3 class="text-primary weight-600 mb-2">Upload Documents</h3>
                <div
                  v-for="(doc, index) in supplementalDocs"
                  :key="index"
                  class="doc-drop-zone mb-3 pa-3 rounded-lg d-flex align-center"
                  :class="{
                    'doc-drop-zone--active': dragOverIndex === index && !doc.links.length,
                  }"
                  @dragover.prevent
                  @dragenter.prevent="onDragEnter(index)"
                  @dragleave.prevent="onDragLeave"
                  @drop.prevent="handleFileDrop(index, $event)">
                  <input
                    :ref="(el) => (fileInputRefs[index] = el)"
                    type="file"
                    accept=".pdf,.tif,.tiff,.jpg,.jpeg,.png"
                    style="display: none"
                    @change="handleFileSelect(index, $event)" />
                  <v-icon class="mr-3 flex-shrink-0" :color="doc.links.length ? 'success' : 'grey'">
                    {{ doc.links.length ? "mdi-check-circle" : "mdi-file-outline" }}
                  </v-icon>
                  <div class="flex-grow-1" style="min-width: 0">
                    <v-text-field
                      v-model="doc.label"
                      placeholder="No file selected"
                      variant="plain"
                      density="compact"
                      hide-details
                      class="text-body-2" />
                    <div v-if="doc.error" class="text-caption text-error">{{ doc.error }}</div>
                  </div>
                  <v-btn
                    v-if="!doc.links.length"
                    variant="tonal"
                    size="small"
                    color="primary"
                    prepend-icon="mdi-upload"
                    class="ml-2 flex-shrink-0"
                    :loading="doc.uploading"
                    @click="fileInputRefs[index]?.click()">
                    Upload
                  </v-btn>
                  <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="x-small"
                    color="error"
                    class="ml-1 flex-shrink-0"
                    @click="supplementalDocs.splice(index, 1)" />
                </div>
                <v-btn
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-plus"
                  class="mb-4"
                  @click="supplementalDocs.push({ label: null, links: [], file: null, uploading: false, error: null })">
                  Add Document
                </v-btn>

                <v-btn
                  color="primary"
                  block
                  size="large"
                  type="submit"
                  :loading="isSubmitting"
                  :disabled="isSubmitting || !!createdOrderId">
                  Run Clinical Review
                </v-btn>

                <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mt-4">
                  <div v-for="error in errors" :key="error">{{ error }}</div>
                </v-alert>

                <v-alert v-if="successMessage" type="success" variant="tonal" class="mt-4">
                  {{ successMessage }}
                </v-alert>

                <div v-if="createdOrderId" class="d-flex ga-3 mt-4">
                  <v-btn color="primary" variant="outlined" @click="viewOrder">View Clinical Review</v-btn>
                  <v-btn color="primary" variant="tonal" @click="resetForm">Start Another</v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { intakeService } from "@/services/intakeService";
import { uploadService, validateFile, parseUploadError, needsServerConversion } from "@/services/uploadService";
import { tryParseApiErrors } from "@/utils";
import { localDateToIso } from "@/utils/dateUtils";
import { usPhoneRule, valueInItemsAutocompleteRule } from "@/utils/form";
import { isNPIValid } from "@/utils/validators";
import { uuid } from "vue-uuid";
import executor from "@/services/api";
import TextDateInput from "@/components/TextDateInput.vue";
import RemoteAutocomplete from "@/components/RemoteAutocomplete.vue";

const router = useRouter();
const formRef = ref(null);
const MAX_PDF_SIZE_BYTES = 30 * 1024 * 1024;
const MAX_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;

const patientFirstName = ref("");
const patientLastName = ref("");
const patientDobDate = ref(null);
const referralStatus = ref(null);
const providerNpi = ref("");
const drugName = ref("");
const diagnosisIcd10Code = ref("");
const phoneNumber = ref("");
const payorName = ref("");

const referralStatusItems = [
  { title: "Initial Referral", value: "initial" },
  { title: "Continuation of Therapy", value: "continuation" },
];

const supplementalDocs = ref([{ label: null, links: [], file: null, uploading: false, error: null }]);
const fileInputRefs = ref({});
const isSubmitting = ref(false);
const errors = ref([]);
const successMessage = ref(null);
const createdOrderId = ref(null);
const dragOverIndex = ref(null);
let dragEnterCount = 0;

const rules = {
  required: (v) => !!v || "This field is required",
  npi: (v) => !v || isNPIValid(v, true) || "Enter a valid 10-digit NPI",
};

const icd10CodeRule = (v, remote) => valueInItemsAutocompleteRule(v, "Enter a valid ICD-10 code", remote);

const onChangeIcd10Code = (value) => {
  if (value.length > 8) return;
  return executor
    .get(`/requirements/search-icd10/?q=${value}`)
    .then((response) => response.results.map((c) => ({ title: `${c.code} - ${c.name}`, value: c.code })));
};

const isAnyUploadInProgress = () => supplementalDocs.value.some((doc) => doc.uploading);
const documentLinks = () => supplementalDocs.value.flatMap((doc) => doc.links);

const patientDobIso = computed(() => localDateToIso(patientDobDate.value));

const onDragEnter = (index) => {
  dragEnterCount++;
  dragOverIndex.value = index;
};

const onDragLeave = () => {
  dragEnterCount--;
  if (dragEnterCount <= 0) {
    dragEnterCount = 0;
    dragOverIndex.value = null;
  }
};

const processFile = async (index, file) => {
  const doc = supplementalDocs.value[index];
  doc.error = null;
  if (!file) return;
  const maxSizeBytes = needsServerConversion(file, file.name) ? MAX_IMAGE_SIZE_BYTES : MAX_PDF_SIZE_BYTES;
  const validationError = validateFile(file, { maxSizeBytes });
  if (validationError) {
    doc.error = validationError;
    return;
  }
  const uploadKey = uuid.v4() + "/" + file.name;
  doc.uploading = true;
  try {
    const link = await uploadService.uploadFile(file, uploadKey);
    doc.links = [link];
    doc.label = file.name;
    doc.error = null;
  } catch (error) {
    doc.error = parseUploadError(error);
  } finally {
    doc.uploading = false;
  }
};

const handleFileDrop = async (index, event) => {
  dragOverIndex.value = null;
  dragEnterCount = 0;
  const doc = supplementalDocs.value[index];
  if (doc.links.length) {
    doc.error = "This document already has a file. Remove it first to upload a new one.";
    return;
  }
  const file = event.dataTransfer?.files?.[0];
  if (file) await processFile(index, file);
};

const handleFileSelect = async (index, event) => {
  const file = event.target.files[0];
  await processFile(index, file);
  event.target.value = "";
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  errors.value = [];

  const { valid } = await formRef.value.validate();
  if (!valid) return;

  if (isAnyUploadInProgress()) {
    errors.value = ["Please wait for file uploads to complete before continuing."];
    return;
  }

  if (documentLinks().length === 0) {
    errors.value = ["At least one document is required."];
    return;
  }

  errors.value = [];
  successMessage.value = null;
  isSubmitting.value = true;
  try {
    const payload = {
      patient_first_name: patientFirstName.value.trim(),
      patient_last_name: patientLastName.value.trim(),
      patient_dob: patientDobIso.value,
      referral_status: referralStatus.value,
      provider_npi: providerNpi.value.trim(),
      drug_name: drugName.value.trim(),
      diagnosis_icd10_code: diagnosisIcd10Code.value.trim(),
      document_links: documentLinks(),
    };
    const phone = phoneNumber.value?.trim();
    if (phone) payload.phone_number = phone;
    const payor = payorName.value?.trim();
    if (payor) payload.payor_name = payor;

    const response = await intakeService.postQuickStartClinicalReview(payload);
    const orderId = response?.id;
    if (!orderId) {
      errors.value = ["Clinical review was started but no order was returned. Please check the intake grid."];
      return;
    }
    createdOrderId.value = orderId;
    successMessage.value = "Clinical review started.";
  } catch (error) {
    errors.value = tryParseApiErrors(error);
    if (errors.value.length === 0) {
      errors.value = ["An unexpected error occurred. Please try again."];
    }
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  patientFirstName.value = "";
  patientLastName.value = "";
  patientDobDate.value = null;
  referralStatus.value = null;
  providerNpi.value = "";
  drugName.value = "";
  diagnosisIcd10Code.value = "";
  phoneNumber.value = "";
  payorName.value = "";
  supplementalDocs.value = [{ label: null, links: [], file: null, uploading: false, error: null }];
  errors.value = [];
  successMessage.value = null;
  createdOrderId.value = null;
  dragOverIndex.value = null;
  dragEnterCount = 0;
  formRef.value?.resetValidation();
};

const viewOrder = () => {
  router.push({ name: "document-review-order", params: { id: createdOrderId.value } });
};
</script>

<style lang="scss" scoped>
@import "@/styles/_variables";

.doc-drop-zone {
  border: 1px solid $lh_dash_border;
  background: transparent;
  transition: border $lh_transition_fast, background $lh_transition_fast;

  &--active {
    border: 2px dashed $lh_color_blue;
    background: rgba($lh_color_blue, 0.04);
  }
}
</style>
